import { Loading } from "@/components";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  
  // Statistics state
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalWords: 0,
    totalSentences: 0,
    totalDictationCourses: 0,
    totalWordCourses: 0,
    pendingUsers: 0,
    recentUsers: []
  });

  const fetchDashboardStats = async () => {
    dispatch(setIsLoading(true));
    try {
      // Fetch users data
      const usersResponse = await API.get('users');
      const users = usersResponse.data.data || [];
      
      // Calculate user statistics
      const totalUsers = users.length;
      const activeUsers = users.filter(user => user.status === '有効').length;
      const pendingUsers = users.filter(user => user.status === '承認待ち').length;
      const recentUsers = users.slice(0, 5); // Get 5 most recent users

      // Fetch content statistics
      const [wordsResponse, sentencesResponse, dictationCoursesResponse, wordCoursesResponse] = await Promise.allSettled([
        API.get('words?limit=1'),
        API.get('sentences?limit=1'),
        API.get('dictation-courses?limit=1'),
        API.get('word-courses?limit=1')
      ]);

      const totalWords = wordsResponse.status === 'fulfilled' ? wordsResponse.value.data.data?.total || 0 : 0;
      const totalSentences = sentencesResponse.status === 'fulfilled' ? sentencesResponse.value.data.data?.total || 0 : 0;
      const totalDictationCourses = dictationCoursesResponse.status === 'fulfilled' ? dictationCoursesResponse.value.data.data?.total || 0 : 0;
      const totalWordCourses = wordCoursesResponse.status === 'fulfilled' ? wordCoursesResponse.value.data.data?.total || 0 : 0;

      setStats({
        totalUsers,
        activeUsers,
        totalWords,
        totalSentences,
        totalDictationCourses,
        totalWordCourses,
        pendingUsers,
        recentUsers
      });

    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const StatCard = ({ title, value, icon, color, link }: {
    title: string;
    value: number | string;
    icon: string;
    color: string;
    link?: string;
  }) => (
    <div className="col-xl-3 col-lg-6 col-sm-6">
      <div className="widget-stat card">
        <div className="card-body p-4">
          <div className="media ai-icon-primary">
            <span className={`me-3 bgl-${color} text-${color}`}>
              <i className={`${icon}`}></i>
            </span>
            <div className="media-body">
              <p className="mb-1">{title}</p>
              <h4 className="mb-0">{value}</h4>
              {link && (
                <span className="badge badge-primary">
                  <a href={link} className="text-white text-decoration-none">詳細を見る</a>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon, link, color }: {
    title: string;
    description: string;
    icon: string;
    link: string;
    color: string;
  }) => (
    <div className="col-xl-4 col-lg-6 col-md-6">
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center">
            <div className={`icon-box icon-box-lg bg-${color}-light me-3`}>
              <i className={`${icon} text-${color}`}></i>
            </div>
            <div>
              <h5 className="mb-1">{title}</h5>
              <p className="mb-0 text-muted">{description}</p>
            </div>
          </div>
          <div className="mt-3">
            <a href={link} className={`btn btn-${color} btn-sm`}>
              <i className="fas fa-arrow-right me-2"></i>
              アクセス
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>ダッシュボード</title>
      </Helmet>
      <div className="container-fluid">
        <Loading />
        
        {!loading && (
          <>
            {/* Welcome Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card bg-primary text-white">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div>
                        <h4 className="text-white mb-2">Studict 管理システムへようこそ</h4>
                        <p className="mb-0">システムの概要と主要な統計情報をご確認いただけます。</p>
                      </div>
                      <div className="ms-auto">
                        <i className="fas fa-chart-line fa-3x opacity-50"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mb-4">
              <StatCard
                title="総ユーザー数"
                value={stats.totalUsers}
                icon="fas fa-users"
                color="primary"
                link="/users"
              />
              <StatCard
                title="アクティブユーザー"
                value={stats.activeUsers}
                icon="fas fa-user-check"
                color="success"
                link="/users"
              />
              <StatCard
                title="承認待ちユーザー"
                value={stats.pendingUsers}
                icon="fas fa-user-clock"
                color="warning"
                link="/users"
              />
              <StatCard
                title="総単語数"
                value={stats.totalWords}
                icon="fas fa-book"
                color="info"
                link="/content/words"
              />
            </div>

            <div className="row mb-4">
              <StatCard
                title="総センテンス数"
                value={stats.totalSentences}
                icon="fas fa-quote-right"
                color="secondary"
                link="/content/sentences"
              />
              <StatCard
                title="ディクテーションコース"
                value={stats.totalDictationCourses}
                icon="fas fa-headphones"
                color="purple"
                link="/content/dictation-courses"
              />
              <StatCard
                title="単語コース"
                value={stats.totalWordCourses}
                icon="fas fa-graduation-cap"
                color="orange"
                link="/content/word-courses"
              />
              <StatCard
                title="システム状態"
                value="正常"
                icon="fas fa-server"
                color="success"
              />
            </div>

            {/* Quick Actions */}
            <div className="row mb-4">
              <div className="col-12">
                <h5 className="mb-3">
                  <i className="fas fa-bolt me-2"></i>
                  クイックアクション
                </h5>
              </div>
              <QuickActionCard
                title="ユーザー管理"
                description="ユーザーの追加、編集、削除"
                icon="fas fa-users-cog"
                link="/users"
                color="primary"
              />
              <QuickActionCard
                title="学習コンテンツ"
                description="単語、センテンス、コースの管理"
                icon="fas fa-book-open"
                link="/content"
                color="success"
              />
              <QuickActionCard
                title="通知管理"
                description="プッシュ通知の送信と管理"
                icon="fas fa-bell"
                link="/notifications"
                color="warning"
              />
            </div>

            {/* Recent Activity */}
            <div className="row">
              <div className="col-xl-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">
                      <i className="fas fa-clock me-2"></i>
                      最近登録されたユーザー
                    </h5>
                  </div>
                  <div className="card-body">
                    {stats.recentUsers.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>ユーザーID</th>
                              <th>ニックネーム</th>
                              <th>メールアドレス</th>
                              <th>ステータス</th>
                              <th>登録日</th>
                              <th>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.recentUsers.map((user: any) => (
                              <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.nickname || '-'}</td>
                                <td>{user.email}</td>
                                <td>
                                  <span className={`badge ${
                                    user.status === '有効' ? 'badge-success' :
                                    user.status === '承認待ち' ? 'badge-warning' :
                                    user.status === '退会済み' ? 'badge-danger' :
                                    'badge-secondary'
                                  }`}>
                                    {user.status}
                                  </span>
                                </td>
                                <td>{user.addDate}</td>
                                <td>
                                  <a href={`/users/${user.id}`} className="btn btn-primary btn-sm">
                                    <i className="fas fa-eye"></i>
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">データがありません。</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-xl-4">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">
                      <i className="fas fa-link me-2"></i>
                      便利なリンク
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="list-group list-group-flush">
                      <a href="/content/words" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div>
                          <i className="fas fa-book me-2 text-primary"></i>
                          単語・熟語管理
                        </div>
                        <i className="fas fa-chevron-right text-muted"></i>
                      </a>
                      <a href="/content/sentences" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div>
                          <i className="fas fa-quote-right me-2 text-success"></i>
                          センテンス管理
                        </div>
                        <i className="fas fa-chevron-right text-muted"></i>
                      </a>
                      <a href="/content/dictation-courses" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div>
                          <i className="fas fa-headphones me-2 text-info"></i>
                          ディクテーションコース
                        </div>
                        <i className="fas fa-chevron-right text-muted"></i>
                      </a>
                      <a href="/content/word-courses" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div>
                          <i className="fas fa-graduation-cap me-2 text-warning"></i>
                          単語コース
                        </div>
                        <i className="fas fa-chevron-right text-muted"></i>
                      </a>
                      <a href="/settings/admins" className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div>
                          <i className="fas fa-user-shield me-2 text-danger"></i>
                          管理者管理
                        </div>
                        <i className="fas fa-chevron-right text-muted"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;
